import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import MenuBar from '@/components/MenuBar';

type Scenario = {
  adsPerDay: number;
  pricePerAd: number;
  weekPartners: number;
  brandedPerMonth: number;
};

const scenarios: Record<string, Scenario> = {
  premium: {
    adsPerDay: 1,
    pricePerAd: 9000,
    weekPartners: 12,
    brandedPerMonth: 0,
  },
  hybrid: {
    adsPerDay: 2,
    pricePerAd: 6000,
    weekPartners: 12,
    brandedPerMonth: 2,
  },
  growth: {
    adsPerDay: 3,
    pricePerAd: 5000,
    weekPartners: 24,
    brandedPerMonth: 4,
  },
};

const AdEconomyPage = () => {
  const [operatingCosts, setOperatingCosts] = useState(3000000);
  const [adsPerDay, setAdsPerDay] = useState(1);
  const [pricePerAd, setPricePerAd] = useState(9000);
  const [viewsPerAd, setViewsPerAd] = useState(8000);
  const [weekPartners, setWeekPartners] = useState(12);
  const [pricePerWeekPartner, setPricePerWeekPartner] = useState(45000);
  const [brandedPerMonth, setBrandedPerMonth] = useState(2);
  const [pricePerBranded, setPricePerBranded] = useState(15000);

  const applyScenario = (scenarioKey: string) => {
    const scenario = scenarios[scenarioKey];
    setAdsPerDay(scenario.adsPerDay);
    setPricePerAd(scenario.pricePerAd);
    setWeekPartners(scenario.weekPartners);
    setBrandedPerMonth(scenario.brandedPerMonth);
  };

  // Calculations
  const dailyRevenue = adsPerDay * pricePerAd;
  const yearlyRevenue = dailyRevenue * 365;
  const additionalRevenue = (weekPartners * pricePerWeekPartner) + (brandedPerMonth * pricePerBranded * 12);
  const totalRevenue = yearlyRevenue + additionalRevenue;
  const eCPM = (pricePerAd / viewsPerAd) * 1000;
  const breakEvenDiff = totalRevenue - operatingCosts;
  const coveragePercent = (totalRevenue / operatingCosts) * 100;
  const isProfit = breakEvenDiff >= 0;

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <h1 className="text-3xl font-bold mb-2">Annonseøkonomi</h1>
        <p className="text-muted-foreground mb-6 max-w-2xl">
          Utforsk hvordan ulike annonsemodeller dekker driftskostnadene. Justér parametere som pris, antall annonser og tilleggspakker for å finne optimal balanse mellom inntekt og arbeidsmengde.
        </p>

        {/* Scenario Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            onClick={() => applyScenario('premium')}
            variant="outline"
            className="border-red-500 text-red-700 hover:bg-red-50"
          >
            🟥 Lavvolum / premium
          </Button>
          <Button
            onClick={() => applyScenario('hybrid')}
            variant="outline"
            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
          >
            🟨 Hybrid
          </Button>
          <Button
            onClick={() => applyScenario('growth')}
            variant="outline"
            className="border-green-500 text-green-700 hover:bg-green-50"
          >
            🟩 Vekst
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Side - Input Controls */}
          <Card className="rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle>Parametere</CardTitle>
              <CardDescription>Juster verdiene for å simulere ulike scenarier</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Operating Costs */}
              <div className="space-y-2">
                <Label htmlFor="operating-costs">Årlige driftskostnader (NOK)</Label>
                <Input
                  id="operating-costs"
                  type="number"
                  value={operatingCosts}
                  onChange={(e) => setOperatingCosts(Number(e.target.value))}
                  className="font-mono"
                />
              </div>

              <Separator />

              {/* Ads per Day */}
              <div className="space-y-2">
                <Label htmlFor="ads-per-day">
                  Antall annonser per dag: <span className="font-semibold">{adsPerDay}</span>
                </Label>
                <Slider
                  id="ads-per-day"
                  min={1}
                  max={10}
                  step={1}
                  value={[adsPerDay]}
                  onValueChange={(value) => setAdsPerDay(value[0])}
                />
              </div>

              {/* Price per Ad */}
              <div className="space-y-2">
                <Label htmlFor="price-per-ad">
                  Pris per annonse (NOK): <span className="font-semibold">{pricePerAd.toLocaleString()}</span>
                </Label>
                <Slider
                  id="price-per-ad"
                  min={1000}
                  max={20000}
                  step={500}
                  value={[pricePerAd]}
                  onValueChange={(value) => setPricePerAd(value[0])}
                />
              </div>

              {/* Views per Ad */}
              <div className="space-y-2">
                <Label htmlFor="views-per-ad">
                  Antall visninger per annonse: <span className="font-semibold">{viewsPerAd.toLocaleString()}</span>
                </Label>
                <Slider
                  id="views-per-ad"
                  min={1000}
                  max={20000}
                  step={500}
                  value={[viewsPerAd]}
                  onValueChange={(value) => setViewsPerAd(value[0])}
                />
              </div>

              <Separator />

              {/* Week Partners */}
              <div className="space-y-2">
                <Label htmlFor="week-partners">
                  Antall ukepartnere pr. år: <span className="font-semibold">{weekPartners}</span>
                </Label>
                <Slider
                  id="week-partners"
                  min={0}
                  max={52}
                  step={1}
                  value={[weekPartners]}
                  onValueChange={(value) => setWeekPartners(value[0])}
                />
              </div>

              {/* Price per Week Partner */}
              <div className="space-y-2">
                <Label htmlFor="price-week-partner">
                  Pris per ukepartner (NOK): <span className="font-semibold">{pricePerWeekPartner.toLocaleString()}</span>
                </Label>
                <Slider
                  id="price-week-partner"
                  min={10000}
                  max={100000}
                  step={5000}
                  value={[pricePerWeekPartner]}
                  onValueChange={(value) => setPricePerWeekPartner(value[0])}
                />
              </div>

              <Separator />

              {/* Branded Content per Month */}
              <div className="space-y-2">
                <Label htmlFor="branded-month">
                  Branded content per måned: <span className="font-semibold">{brandedPerMonth}</span>
                </Label>
                <Slider
                  id="branded-month"
                  min={0}
                  max={10}
                  step={1}
                  value={[brandedPerMonth]}
                  onValueChange={(value) => setBrandedPerMonth(value[0])}
                />
              </div>

              {/* Price per Branded */}
              <div className="space-y-2">
                <Label htmlFor="price-branded">
                  Pris per branded content (NOK): <span className="font-semibold">{pricePerBranded.toLocaleString()}</span>
                </Label>
                <Slider
                  id="price-branded"
                  min={5000}
                  max={30000}
                  step={1000}
                  value={[pricePerBranded]}
                  onValueChange={(value) => setPricePerBranded(value[0])}
                />
              </div>
            </CardContent>
          </Card>

          {/* Right Side - Results */}
          <Card className="rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle>Resultater</CardTitle>
              <CardDescription>Sanntidsberegninger basert på dine parametere</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Total Revenue */}
              <div className="text-center py-4">
                <div className="text-sm text-muted-foreground mb-2">Totale årlige inntekter</div>
                <div className="text-4xl font-bold text-primary">
                  {totalRevenue.toLocaleString()} NOK
                </div>
              </div>

              {/* Coverage Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dekningsgrad</span>
                  <span className="font-semibold">{coveragePercent.toFixed(1)}%</span>
                </div>
                <Progress value={Math.min(coveragePercent, 100)} className="h-3" />
              </div>

              <Separator />

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{eCPM.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground mt-1">💰 eCPM</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{adsPerDay}</div>
                  <div className="text-xs text-muted-foreground mt-1">📅 Annonser/dag</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className={`text-2xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                    {isProfit ? '+' : ''}{(breakEvenDiff / 1000000).toFixed(2)}M
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {isProfit ? '🟩 Overskudd' : '🟥 Underskudd'}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Summary Message */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-center">
                  {coveragePercent >= 100 ? (
                    <>
                      Med dagens parametere dekker du <span className="font-bold text-green-600">{coveragePercent.toFixed(0)}%</span> av driftskostnadene. 🎉
                    </>
                  ) : (
                    <>
                      Med dagens parametere dekker du kun <span className="font-bold text-orange-600">{coveragePercent.toFixed(0)}%</span> av driftskostnadene. Du trenger flere inntektskilder.
                    </>
                  )}
                </p>
              </div>

              {/* Breakdown */}
              <div className="space-y-3 pt-4">
                <div className="text-sm font-semibold text-muted-foreground">Inntektsfordeling:</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Daglige annonser (365 dager):</span>
                    <span className="font-semibold">{yearlyRevenue.toLocaleString()} NOK</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ukepartnere ({weekPartners} uker):</span>
                    <span className="font-semibold">{(weekPartners * pricePerWeekPartner).toLocaleString()} NOK</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Branded content ({brandedPerMonth * 12} per år):</span>
                    <span className="font-semibold">{(brandedPerMonth * pricePerBranded * 12).toLocaleString()} NOK</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold">
                    <span>Total:</span>
                    <span className="text-primary">{totalRevenue.toLocaleString()} NOK</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdEconomyPage;
